import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Badge,
    Button,
    Card,
    Container,
    Group,
    NumberInput,
    Paper,
    Select,
    Stack,
    Table,
    Text,
    Textarea,
    Title,
} from "@mantine/core";

import {
    IconTrash,
    IconTrashX,
} from "@tabler/icons-react";

import { useParams } from "react-router-dom";

import {
    createFoodWasteLog,
    deleteFoodWasteLog,
    getFoodMenu,
    getFoodWasteLogs,
} from "../../../api/foodApi";

import type {
    FoodMenuItem,
    FoodWasteLog,
    FoodWasteReason,
} from "./foodTypes";

const reasons: {
    value: FoodWasteReason;
    label: string;
}[] = [
        {
            value: "Burned",
            label: "Brændt på",
        },
        {
            value: "Uncollected",
            label: "Uafhentet",
        },
        {
            value: "MadeWrong",
            label: "Forkert lavet",
        },
        {
            value: "Dropped",
            label: "Tabt",
        },
        {
            value: "Other",
            label: "Andet",
        },
    ];

function getReasonLabel(
    reason: FoodWasteReason
) {
    return (
        reasons.find(
            x => x.value === reason
        )?.label ??
        reason
    );
}

function getReasonColor(
    reason: FoodWasteReason
) {
    switch (reason) {
        case "Burned":
            return "red";

        case "Uncollected":
            return "orange";

        case "MadeWrong":
            return "yellow";

        case "Dropped":
            return "grape";

        case "Other":
            return "gray";

        default:
            return "gray";
    }
}

function formatDateTime(
    value: string
) {
    return new Date(
        value
    ).toLocaleString(
        "da-DK",
        {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        }
    );
}

export default function FoodWastePage() {
    const { eventId } =
        useParams<{
            eventId: string;
        }>();

    const parsedEventId =
        Number(eventId);

    const [menu, setMenu] =
        useState<FoodMenuItem[]>([]);

    const [logs, setLogs] =
        useState<FoodWasteLog[]>([]);

    const [selectedProductId, setSelectedProductId] =
        useState<string | null>(null);

    const [quantity, setQuantity] =
        useState<number>(1);

    const [reason, setReason] =
        useState<FoodWasteReason | null>(
            null
        );

    const [note, setNote] =
        useState("");

    const [productFilter, setProductFilter] =
        useState<string | null>(null);

    const [reasonFilter, setReasonFilter] =
        useState<string | null>(null);

    const [busy, setBusy] =
        useState(false);

    const [message, setMessage] =
        useState("");

    async function load() {
        if (
            !Number.isFinite(
                parsedEventId
            ) ||
            parsedEventId <= 0
        ) {
            return;
        }

        try {
            const [
                menuData,
                wasteData,
            ] = await Promise.all([
                getFoodMenu(
                    parsedEventId
                ),
                getFoodWasteLogs(
                    parsedEventId
                ),
            ]);

            setMenu(
                Array.isArray(menuData)
                    ? menuData
                    : []
            );

            setLogs(
                Array.isArray(wasteData)
                    ? wasteData
                    : []
            );
        } catch (err) {
            setMessage(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke hente data."
            );
        }
    }

    useEffect(() => {
        load();
    }, [parsedEventId]);

    const menuOptions =
        useMemo(
            () =>
                menu.map(
                    item => ({
                        value:
                            item.id.toString(),
                        label:
                            item.name,
                    })
                ),
            [menu]
        );

    const visibleLogs =
        useMemo(() => {
            return logs.filter(
                log => {
                    if (
                        productFilter &&
                        log.menuItemId.toString() !==
                        productFilter
                    ) {
                        return false;
                    }

                    if (
                        reasonFilter &&
                        log.reason !==
                        reasonFilter
                    ) {
                        return false;
                    }

                    return true;
                }
            );
        }, [
            logs,
            productFilter,
            reasonFilter,
        ]);

    const totalWaste =
        visibleLogs.reduce(
            (sum, log) =>
                sum +
                log.quantity,
            0
        );

    async function submit() {
        const menuItemId =
            Number(
                selectedProductId
            );

        if (
            !menuItemId ||
            !reason ||
            quantity <= 0
        ) {
            setMessage(
                "Vælg produkt, antal og årsag."
            );

            return;
        }

        setBusy(true);
        setMessage("");

        try {
            await createFoodWasteLog(
                parsedEventId,
                {
                    menuItemId,
                    quantity,
                    reason,
                    note:
                        note.trim(),
                }
            );

            setSelectedProductId(
                null
            );

            setQuantity(1);
            setReason(null);
            setNote("");

            setMessage(
                "Spild registreret."
            );

            await load();

            window.setTimeout(
                () =>
                    setMessage(""),
                2000
            );
        } catch (err) {
            setMessage(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke registrere spild."
            );
        } finally {
            setBusy(false);
        }
    }

    async function remove(
        log: FoodWasteLog
    ) {
        if (
            !window.confirm(
                `Vil du slette spild-log #${log.id}?`
            )
        ) {
            return;
        }

        try {
            await deleteFoodWasteLog(
                parsedEventId,
                log.id
            );

            setLogs(
                prev =>
                    prev.filter(
                        x =>
                            x.id !==
                            log.id
                    )
            );
        } catch (err) {
            setMessage(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke slette log."
            );
        }
    }

    return (
        <Container
            size="xl"
            py="lg"
        >
            <Stack gap="lg">
                <Group
                    justify="space-between"
                    align="flex-start"
                >
                    <div>
                        <Title order={1}>
                            Skraldespand log
                        </Title>

                        <Text c="dimmed">
                            Registrer mad og produkter,
                            der bliver smidt ud.
                        </Text>
                    </div>

                    <Badge
                        size="lg"
                        color="red"
                        variant="light"
                        leftSection={
                            <IconTrash
                                size={15}
                            />
                        }
                    >
                        {totalWaste} stk. vist
                    </Badge>
                </Group>

                {message && (
                    <Paper
                        withBorder
                        p="sm"
                        radius="md"
                    >
                        <Text fw={600}>
                            {message}
                        </Text>
                    </Paper>
                )}

                <Card
                    withBorder
                    radius="md"
                    p="lg"
                >
                    <Stack gap="md">
                        <div>
                            <Title
                                order={3}
                            >
                                Registrer spild
                            </Title>

                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                Vælg produkt,
                                antal og hvorfor
                                det bliver smidt ud.
                            </Text>
                        </div>

                        <Group
                            grow
                            align="end"
                        >
                            <Select
                                label="Produkt"
                                placeholder="Vælg produkt"
                                searchable
                                data={
                                    menuOptions
                                }
                                value={
                                    selectedProductId
                                }
                                onChange={
                                    setSelectedProductId
                                }
                            />

                            <NumberInput
                                label="Antal"
                                min={1}
                                value={
                                    quantity
                                }
                                onChange={
                                    value =>
                                        setQuantity(
                                            Number(
                                                value
                                            ) ||
                                            1
                                        )
                                }
                            />
                        </Group>

                        <div>
                            <Text
                                size="sm"
                                fw={600}
                                mb={6}
                            >
                                Årsag
                            </Text>

                            <Group gap="xs">
                                {reasons.map(
                                    item => (
                                        <Button
                                            key={
                                                item.value
                                            }
                                            variant={
                                                reason ===
                                                    item.value
                                                    ? "filled"
                                                    : "light"
                                            }
                                            color={
                                                getReasonColor(
                                                    item.value
                                                )
                                            }
                                            onClick={() =>
                                                setReason(
                                                    item.value
                                                )
                                            }
                                        >
                                            {
                                                item.label
                                            }
                                        </Button>
                                    )
                                )}
                            </Group>
                        </div>

                        <Textarea
                            label="Kommentar"
                            placeholder="Valgfri kommentar..."
                            value={
                                note
                            }
                            onChange={
                                e =>
                                    setNote(
                                        e.currentTarget.value
                                    )
                            }
                            minRows={2}
                        />

                        <Button
                            size="md"
                            color="red"
                            leftSection={
                                <IconTrashX
                                    size={18}
                                />
                            }
                            loading={
                                busy
                            }
                            disabled={
                                !selectedProductId ||
                                !reason
                            }
                            onClick={
                                submit
                            }
                        >
                            Registrer spild
                        </Button>
                    </Stack>
                </Card>

                <Card
                    withBorder
                    radius="md"
                    p="lg"
                >
                    <Stack gap="md">
                        <Group
                            justify="space-between"
                            align="end"
                        >
                            <div>
                                <Title
                                    order={3}
                                >
                                    Historik
                                </Title>

                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Se hvad der er
                                    blevet smidt ud.
                                </Text>
                            </div>

                            <Group>
                                <Select
                                    placeholder="Alle produkter"
                                    clearable
                                    searchable
                                    data={
                                        menuOptions
                                    }
                                    value={
                                        productFilter
                                    }
                                    onChange={
                                        setProductFilter
                                    }
                                />

                                <Select
                                    placeholder="Alle årsager"
                                    clearable
                                    data={
                                        reasons
                                    }
                                    value={
                                        reasonFilter
                                    }
                                    onChange={
                                        setReasonFilter
                                    }
                                />
                            </Group>
                        </Group>

                        <Table
                            striped
                            highlightOnHover
                            withTableBorder
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>
                                        Tid
                                    </Table.Th>

                                    <Table.Th>
                                        Produkt
                                    </Table.Th>

                                    <Table.Th>
                                        Antal
                                    </Table.Th>

                                    <Table.Th>
                                        Årsag
                                    </Table.Th>

                                    <Table.Th>
                                        Kommentar
                                    </Table.Th>

                                    <Table.Th />
                                </Table.Tr>
                            </Table.Thead>

                            <Table.Tbody>
                                {visibleLogs.length ===
                                    0 ? (
                                    <Table.Tr>
                                        <Table.Td
                                            colSpan={
                                                6
                                            }
                                        >
                                            <Text
                                                ta="center"
                                                c="dimmed"
                                                py="lg"
                                            >
                                                Ingen spild
                                                registreret.
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : (
                                    visibleLogs.map(
                                        log => (
                                            <Table.Tr
                                                key={
                                                    log.id
                                                }
                                            >
                                                <Table.Td>
                                                    {formatDateTime(
                                                        log.createdAt
                                                    )}
                                                </Table.Td>

                                                <Table.Td>
                                                    <Text
                                                        fw={600}
                                                    >
                                                        {
                                                            log.productName
                                                        }
                                                    </Text>
                                                </Table.Td>

                                                <Table.Td>
                                                    <Badge
                                                        color="red"
                                                        variant="light"
                                                    >
                                                        {
                                                            log.quantity
                                                        }{" "}
                                                        stk.
                                                    </Badge>
                                                </Table.Td>

                                                <Table.Td>
                                                    <Badge
                                                        color={
                                                            getReasonColor(
                                                                log.reason
                                                            )
                                                        }
                                                        variant="light"
                                                    >
                                                        {getReasonLabel(
                                                            log.reason
                                                        )}
                                                    </Badge>
                                                </Table.Td>

                                                <Table.Td>
                                                    {
                                                        log.note ||
                                                        "—"
                                                    }
                                                </Table.Td>

                                                <Table.Td
                                                    ta="right"
                                                >
                                                    <Button
                                                        size="xs"
                                                        variant="subtle"
                                                        color="red"
                                                        onClick={() =>
                                                            remove(
                                                                log
                                                            )
                                                        }
                                                    >
                                                        Slet
                                                    </Button>
                                                </Table.Td>
                                            </Table.Tr>
                                        )
                                    )
                                )}
                            </Table.Tbody>
                        </Table>
                    </Stack>
                </Card>
            </Stack>
        </Container>
    );
}